import { PortalModule } from '@angular/cdk/portal';
import { Component, Inject, Type, ViewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { TmLanguageSelectComponent } from '@teammood/shared/ui/language-select';
import { TmOutOfOfficeComponent } from '@teammood/shared/ui/out-of-office';
import { SnackBarService } from '@teammood/shared/ui/snack-bar';
import { TmTimepickerComponent } from '@teammood/shared/ui/timepicker';
import { TmTimeZonePickerComponent } from '@teammood/shared/ui/timezone-picker';
import { TmWorkingDaysComponent } from '@teammood/shared/ui/working-days';
import { TeamMemberService } from '../team-member.service';
import { TeamMember } from '../team-member.type';
import { TeamMemberRolesComponent } from '../team-member-roles/team-member-roles.component';

export type BatchUpdateProperty = 'disable' | 'enable' | 'holidays' | 'language' | 'role' | 'time' | 'timezone' | 'working_days';

@Component({
    selector: 'tm-team-member-batch-update-dialog',
    templateUrl: 'team-member-batch-update-dialog.component.html',
    styleUrls: ['team-member-batch-update-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatDialogActions,
        MatDialogClose,
        MatDialogTitle,
        MatDialogContent,
        MatFormFieldModule,
        MatSelectModule,
        PortalModule,
        ReactiveFormsModule,
        TranslateModule,
        TeamMemberRolesComponent
    ]
})
export class TeamMemberBatchUpdateDialogComponent {

  descriptionKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;

  @ViewChild('componentPlaceholder', { read: ViewContainerRef, static: true })
  public componentPlaceholder!: ViewContainerRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TeamMemberBatchUpdateDialogData,
    public dialogRef: MatDialogRef<TeamMemberBatchUpdateDialogComponent, TeamMember | boolean>,
    private _teamMemberService: TeamMemberService,
    private _snackBarService: SnackBarService,
  ) {
    this.descriptionKey = `please_select_${data.property}`;
  }

  async ngOnInit() {
    const componentType = this.getComponentType(this.data.property);
    const defaultValue = this.getDefaultValue(this.data.property);
    this.value = defaultValue;
    if (componentType) {
      const componentRef = this.componentPlaceholder.createComponent(componentType);
      componentRef.instance.writeValue(defaultValue);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      componentRef.instance.registerOnChange((value: any) => this.value = value);
    }
  }

  updateRole(roles: string[]){
    this.value = roles;
  }

  async update() {
    await this._teamMemberService.batchUpdate(this.data.selectedTeamMembers, this.data.teamId, this.data.property, this.value)
    this.dialogRef.close(true);
    this._snackBarService.success('Settings updated');
  }

  close() {
    this.dialogRef.close();
  }

  private getDefaultValue(property: BatchUpdateProperty): any {
    switch (property) {
      case 'disable':
      case 'enable':
        return 'nothing';
      case 'holidays':
        return [];
      case 'language':
      case 'role':
        return ['member'];
      case 'timezone':
      case 'working_days':
        return null;
      case 'time':
        return '17:00';
    }
  }

  private getComponentType(property: BatchUpdateProperty): Type<ControlValueAccessor> {
    switch (property) {
      case 'disable':
      case 'enable':
        return undefined;
      case 'holidays':
        return TmOutOfOfficeComponent;
      case 'language':
        return TmLanguageSelectComponent;
      case 'role':
        return undefined;
      case 'time':
        return TmTimepickerComponent;
      case 'timezone':
        return TmTimeZonePickerComponent;
      case 'working_days':
        return TmWorkingDaysComponent;
    }
  }

}

export interface TeamMemberBatchUpdateDialogData {
  property: BatchUpdateProperty;
  teamId: string;
  selectedTeamMembers: TeamMember[];
}